import { ChangeEvent, useEffect } from 'react';

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  List,
  ListSubheader,
  Paper,
  Typography,
} from '@mui/material';

import { Analyzer, Snapshot } from 'types';
import { grey } from '@mui/material/colors';
import { Role } from 'utils/userPermissions';

import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useUser } from './api/getUser';
import { useUserPermissionSettings } from './api/getUserPermissionSettings';
import { useCreateAnalyzerPermission } from './api/createAnalyzerPermission';
import { useCreateSnapshotPermission } from './api/createSnapshotPermission';
import { useDeleteAnalyzerPermission } from './api/deleteAnalyzerPermission';
import { useDeleteSnapshotPermission } from './api/deleteSnapshotPermission';
import { useUpdateUserPermissionSettings } from './api/updateUserPermissionSettings';
import AnalyzerPermissionsContainer from './AnalyzerPermissionsContainer';
import { usePermissionsList } from './hooks/usePermissionsList';
import { PermissionActionTypes } from './types';
import { useSnapshotFilterBar } from 'features/snapshots/SnapshotFilterBar';

interface Props {
  selectedUserId: number | null;
}

const UserPermissionManagementSection = ({ selectedUserId }: Props) => {
  const { data: analyzers } = useAnalyzers({});
  const { data: user } = useUser({
    userId: selectedUserId || -1,
  });
  const { data: permissionSettings } = useUserPermissionSettings({});
  const createAnalyzerPermissionMutation = useCreateAnalyzerPermission({});
  const createSnapshotPermissionMutation = useCreateSnapshotPermission({});
  const deleteAnalyzerPermissionMutation = useDeleteAnalyzerPermission({
    userId: selectedUserId || -1,
  });
  const deleteSnapshotPermissionMutation = useDeleteSnapshotPermission({
    userId: selectedUserId || -1,
  });
  const updatePermissionSettingMutation = useUpdateUserPermissionSettings({});

  const { userPermissions, dispatchUserPermissions, hasChanges } =
    usePermissionsList();

  const {
    SnapshotFilterBar: DraftSnapshotFilterBar,
    getFilterSnapshotsByName,
  } = useSnapshotFilterBar();
  const SnapshotFilterBar = permissionSettings?.snapshot_compartmentalization
    ? DraftSnapshotFilterBar
    : null;

  useEffect(() => {
    if (user) {
      const permittedAnalyzerIds = user.user_analyzer_permission.map(
        (permission) => permission.analyzer_id
      );
      const permittedSnapshotIds = user.user_snapshot_permission.map(
        (permission) => permission.snapshot_id
      );
      dispatchUserPermissions({
        type: PermissionActionTypes.RESET,
        resetState: { permittedSnapshotIds, permittedAnalyzerIds },
      });
    }
  }, [user, dispatchUserPermissions]);

  const handleSubmit = async () => {
    if (!user || !selectedUserId) return;
    let analyzerPermissionsToSubmit = [...userPermissions.permittedAnalyzerIds];
    let snapshotPermissionsToSubmit = [...userPermissions.permittedSnapshotIds];
    if (user.user_analyzer_permission) {
      for (const permission of user.user_analyzer_permission) {
        if (!analyzerPermissionsToSubmit.includes(permission.analyzer_id))
          deleteAnalyzerPermissionMutation.mutateAsync({
            userId: selectedUserId,
            analyzerId: permission.analyzer_id,
          });
        analyzerPermissionsToSubmit = analyzerPermissionsToSubmit.filter(
          (analyzerId) => analyzerId !== permission.analyzer_id
        );
      }
    }
    for (const analyzerId of analyzerPermissionsToSubmit) {
      createAnalyzerPermissionMutation.mutateAsync({
        userId: selectedUserId,
        analyzerId,
      });
    }
    if (user.user_snapshot_permission) {
      for (const permission of user.user_snapshot_permission) {
        if (!snapshotPermissionsToSubmit.includes(permission.snapshot_id))
          deleteSnapshotPermissionMutation.mutateAsync({
            userId: selectedUserId,
            snapshotId: permission.snapshot_id,
          });
        snapshotPermissionsToSubmit = snapshotPermissionsToSubmit.filter(
          (snapshotId) => snapshotId !== permission.snapshot_id
        );
      }
    }
    for (const snapshotId of userPermissions.permittedSnapshotIds) {
      createSnapshotPermissionMutation.mutateAsync({
        userId: selectedUserId,
        snapshotId,
      });
    }
    dispatchUserPermissions({
      type: PermissionActionTypes.RESET,
      resetState: userPermissions,
    });
  };

  const checkIfAnalyzerSelected = (analyzer: Analyzer) => {
    return userPermissions.permittedAnalyzerIds.includes(analyzer.id);
  };

  const checkIfSnapshotSelected = (snapshot: Snapshot) => {
    return userPermissions.permittedSnapshotIds.includes(snapshot.id);
  };

  const updateAnalyzerPermissions = (analyzerId: number, checked: boolean) => {
    dispatchUserPermissions({
      type: PermissionActionTypes.ANALYZER,
      checkedState: { id: analyzerId, checked },
    });
  };

  const updateSnapshotPermissions = (snapshotId: number, checked: boolean) => {
    dispatchUserPermissions({
      type: PermissionActionTypes.SNAPSHOT,
      checkedState: { id: snapshotId, checked },
    });
  };

  const handleSnapshotPermissionsChecked = async (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!user) return;
    await updatePermissionSettingMutation.mutateAsync({
      data: {
        snapshot_compartmentalization: event.target.checked,
      },
      userId: user.id,
    });
  };

  if (user?.frozen) {
    return (
      <Grid container item xs direction='column' spacing={2}>
        <Grid item container>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!permissionSettings?.snapshot_compartmentalization}
                onChange={handleSnapshotPermissionsChecked}
                name='snapshot_compartmentalization'
              />
            }
            label='Snapshot-Based Permissions'
          />
        </Grid>
        <Grid item container>
          <Grid container item component={Paper}>
            <Grid item xs>
              <Typography sx={{ p: '2rem' }} color={grey[500]}>
                User has been frozen. Restore access from the edit user dialog.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container item xs direction='column' spacing={2}>
      <Grid item container>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!permissionSettings?.snapshot_compartmentalization}
              onChange={handleSnapshotPermissionsChecked}
              name='snapshot_compartmentalization'
            />
          }
          label='Snapshot-Based Permissions'
        />
      </Grid>
      <Grid item container>
        <Grid container item component={Paper}>
          {user !== null && user !== undefined && user?.role === Role.ADMIN ? (
            <Grid item xs>
              <Typography sx={{ p: '2rem' }} color={grey[500]}>
                Admins have access to all analyzers and snapshots.
              </Typography>
            </Grid>
          ) : user !== null && user !== undefined ? (
            <>
              <Grid
                direction='column'
                container
                sx={{ overflow: 'auto', maxHeight: '80vh' }}
                item
                wrap='nowrap'
              >
                <Grid item>
                  <List
                    style={{ overflow: 'auto', maxHeight: '75vh' }}
                    subheader={
                      <ListSubheader
                        component='div'
                        id='nested-list-subheader'
                        sx={{ borderRadius: '10px' }}
                      >
                        <Grid container justifyContent='space-between'>
                          Analyzers
                          {SnapshotFilterBar}
                        </Grid>
                      </ListSubheader>
                    }
                  >
                    {analyzers?.map((analyzer) => (
                      <AnalyzerPermissionsContainer
                        key={analyzer.id}
                        analyzerId={analyzer.id}
                        updateAnalyzerPermissions={updateAnalyzerPermissions}
                        updateSnapshotPermissions={updateSnapshotPermissions}
                        checkIfAnalyzerSelected={checkIfAnalyzerSelected}
                        checkIfSnapshotSelected={checkIfSnapshotSelected}
                        getFilterSnapshots={getFilterSnapshotsByName}
                      />
                    ))}
                  </List>
                </Grid>
                <Grid
                  item
                  container
                  style={{ paddingBottom: '.8rem', paddingRight: '1.6rem' }}
                  justifyContent='flex-end'
                >
                  <Grid item xs={4} style={{ minWidth: '10rem' }}>
                    <Button
                      disabled={!hasChanges}
                      fullWidth
                      variant='contained'
                      onClick={handleSubmit}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid item xs>
              <Typography sx={{ p: '2rem' }} color={grey[500]}>
                Select a user to manage permissions.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default UserPermissionManagementSection;
